<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useMeta } from "@/composables/use-meta";
import axios from 'axios';
import { BASE_URL, BASE_URL_2 } from '@/Config/config';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Swal from 'sweetalert2';

useMeta({ title: "Reports" });

const store = useStore();

const searchValue = ref('');
const startDate = ref('');
const endDate = ref('');
const selectedViolationId = ref('');

// Loading states
const isLoading = ref(false);
const loading = ref(false);

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(20);
const totalPages = ref(1);

const violations = ref([]);
const parameters = ref([]);
const violationActions = ref([]);
const violationParameters = ref([]);
const drivers = ref([]);
const trucks = ref([]);
const trailers = ref([]);
const loading_order_item = ref([]);
const actionsTakenList = ref([]);

// Search Values
const violationSearch = ref('');
const employeeSearch = ref('');
const truckSearch = ref('');
const trailerSearch = ref('');
const loadingOrderSearch = ref('');
const actionsTakenSearch = ref('');

// Initialize modal
let createViolationActionModal = null;

// SweetAlert functions
// Create the toast instance
const toast = window.Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    padding: '2em'
});

const showSuccessAlert = (message) => {
    toast.fire({
        icon: 'success',
        title: message
    });
};

const showErrorAlert = (message) => {
    toast.fire({
        icon: 'error',
        title: message
    });
};

const openViolationModal = () => {
    if (!createViolationActionModal) {
        const el = document.getElementById('addViolationActionModal');
        if (el) {
            createViolationActionModal = new Modal(el);
        }
    }
    createViolationActionModal?.show();
};

const violationAction = ref({
    violation_id: '',
    truck_id: '',
    trailer_id: '',
    loading_order_item_id: '',
    taken_action_id: '',
    time: '',
    location: '',
    employee_id: '',
    parameters: {}
});

// Helper functions for Multi-Select
const filteredViolations = computed(() => {
    if (!violationSearch.value) return violations.value;
    const term = violationSearch.value.toLowerCase();
    return violations.value.filter(violation =>
        violation.title.toLowerCase().includes(term))
});

const filteredDrivers = computed(() => {
    if (!employeeSearch.value) return drivers.value;
    const term = employeeSearch.value.toLowerCase();
    return drivers.value.filter(employee =>
        employee.driver.toLowerCase().includes(term))
});

const filteredTrucks = computed(() => {
    if (!truckSearch.value) return trucks.value;
    const term = truckSearch.value.toLowerCase();
    return trucks.value.filter(truck =>
        truck.registration_number.toLowerCase().includes(term))
});

const filteredTrailers = computed(() => {
    if (!trailerSearch.value) return trailers.value;
    const term = trailerSearch.value.toLowerCase();
    return trailers.value.filter(trailer =>
        trailer.registration_number.toLowerCase().includes(term))
});

const filteredLoadingOrders = computed(() => {
    if (!loadingOrderSearch.value) return loading_order_item.value;

    const term = loadingOrderSearch.value.toLowerCase();
    return loading_order_item.value.filter(item => {
        return (
            String(item.id).toLowerCase().includes(term) ||
            (item.truck && item.truck.toLowerCase().includes(term)) ||
            (item.trailer && item.trailer.toLowerCase().includes(term)) ||
            (item.type && item.type.toLowerCase().includes(term))
        );
    });
});

const loadingOrderLabel = (item) => {
    return `ID: ${item.id} | Truck: ${item.truck} | Trailer: ${item.trailer} | Type: ${item.type}`;
};

const filteredActionsTaken = computed(() => {
    if (!actionsTakenSearch.value) return actionsTakenList.value;
    const term = actionsTakenSearch.value.toLowerCase();
    return actionsTakenList.value.filter(action =>
        action.name.toLowerCase().includes(term))
});

const filteredParams = computed(() => {
    if (!selectedViolationId.value) return [];
    return violationParameters.value.filter(param =>
        param.violation_id === selectedViolationId.value && param.is_active
    );
});

const filteredActions = computed(() => {
    let filtered = violationActions.value;

    // Filter by violation
    if (selectedViolationId.value) {
        filtered = filtered.filter(action => action.violation_id == selectedViolationId.value);
    }

    // Filter by date range
    if (startDate.value && endDate.value) {
        filtered = filtered.filter(action => {
            const actionDate = action.created_at ? new Date(action.created_at) : null;
            if (!actionDate) return false;
            return (
                actionDate >= new Date(startDate.value) &&
                actionDate <= new Date(endDate.value + 'T23:59:59')
            );
        });
    }

    // Filter by search term
    if (searchValue.value) {
        const keyword = searchValue.value.toLowerCase();
        filtered = filtered.filter(action => {
            const driver = drivers.value.find(d => d.employee_id == action.employee_id);
            const truck = trucks.value.find(t => t.id == action.truck_id);
            const trailer = trailers.value.find(t => t.id == action.trailer_id);

            return (
                (driver?.driver || '').toLowerCase().includes(keyword) ||
                (truck?.registration_number || '').toLowerCase().includes(keyword) ||
                (trailer?.registration_number || '').toLowerCase().includes(keyword) ||
                (action.location || '').toLowerCase().includes(keyword) ||
                (action.taken_action?.name || '').toLowerCase().includes(keyword) ||
                (action.violation?.title || '').toLowerCase().includes(keyword)
            );
        });
    }

    // Update total pages whenever filtered data changes
    totalPages.value = Math.ceil(filtered.length / itemsPerPage.value);

    return filtered;
});

const loadActionsTaken = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/taken-actions`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        actionsTakenList.value = response.data;
        console.log('Actions taken responded:', response.status);
    } catch (error) {
        console.error('Error loading actions taken:', error);
        showErrorAlert('Failed to load actions taken. Please try again later.');
    }
};

const fetchLoadingOrder = async () => {
    try {
        loading.value = true;
        const response = await axios.get(`${BASE_URL_2}/loading-order-item/show`);
        loading_order_item.value = response.data.loading_order_items;
        console.log("Loading orders responded:", response.status);
    } catch (error) {
        console.error('Error fetching loading order items:', error);
        showErrorAlert('Failed to load loading orders. Please try again later.');
    } finally {
        loading.value = false;
    }
};

// Computed property for paginated data
const paginatedActions = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredActions.value.slice(start, end);
});

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

const getDriverName = (employeeId) => {
    const driver = drivers.value.find(d => d.employee_id == employeeId);
    return driver?.driver || 'N/A';
};

const getTruckNumber = (truckId) => {
    const truck = trucks.value.find(t => t.id == truckId);
    return truck?.registration_number || 'N/A';
};

const getTrailerNumber = (trailerId) => {
    const trailer = trailers.value.find(t => t.id == trailerId);
    return trailer?.registration_number || 'N/A';
};

// Pagination methods
const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const firstPage = () => {
    currentPage.value = 1;
};

const lastPage = () => {
    currentPage.value = totalPages.value;
};

// Add these computed properties to your script section
const visiblePages = computed(() => {
    const totalVisible = 10; // Show 10 pages at a time
    const half = Math.floor(totalVisible / 2);
    let start = Math.max(currentPage.value - half, 1);
    let end = Math.min(start + totalVisible - 1, totalPages.value);

    // Adjust if we're near the end
    if (end - start + 1 < totalVisible) {
        start = Math.max(end - totalVisible + 1, 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    return pages;
});

const showStartEllipsis = computed(() => visiblePages.value[0] > 2);
const showEndEllipsis = computed(() => visiblePages.value[visiblePages.value.length - 1] < totalPages.value - 1);

// Watch for filter changes and reset to first page
watch([searchValue, startDate, endDate, selectedViolationId], () => {
    currentPage.value = 1;
});

// Watch violation_id and load parameters
watch(() => violationAction.value.violation_id, async (newVal) => {
    const violationId = newVal?.id || newVal;
    if (violationId) {
        try {
            const response = await axios.get(`${BASE_URL}/violations/${violationId}`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });
            const violationData = response.data;

            parameters.value = violationData.parameters || [];
            violationAction.value.parameters = {};

            parameters.value.forEach(param => {
                violationAction.value.parameters[param.name] = '';
            });

            console.log('Parameters loaded:', parameters.value);
        } catch (error) {
            console.error('Error loading violation parameters:', error);
            parameters.value = [];
            violationAction.value.parameters = {};
            showErrorAlert('Failed to load parameters for this violation');
        }
    } else {
        parameters.value = [];
        violationAction.value.parameters = {};
    }
}, { immediate: true });

const submitReport = async () => {
    try {
        loading.value = true;

        // Prepare the payload with string IDs
        const payload = {
            ...violationAction.value,
            employee_id: violationAction.value.employee_id?.employee_id?.toString() || '',
            truck_id: violationAction.value.truck_id?.id?.toString() || '',
            trailer_id: violationAction.value.trailer_id?.id?.toString() || '',
            loading_order_item_id: violationAction.value.loading_order_item_id?.id?.toString() || '',
            taken_action_id: typeof violationAction.value.taken_action_id === 'object'
                ? violationAction.value.taken_action_id.id
                : violationAction.value.taken_action_id,
            violation_id: typeof violationAction.value.violation_id === 'object'
                ? violationAction.value.violation_id.id
                : violationAction.value.violation_id,
            parameters: violationAction.value.parameters,
            truck: violationAction.value.truck_id?.registration_number || '',
            trailer: violationAction.value.trailer_id?.registration_number || '',
            employee: violationAction.value.employee_id?.driver || '',
            cargo_type: violationAction.value.loading_order_item_id?.cargo_type || '',
            direction: violationAction.value.loading_order_item_id?.type || ''
        };

        console.log('Submitting payload:', payload);

        const response = await axios.post(`${BASE_URL}/violation-actions`,
            payload, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        if (response.data.success) {
            showSuccessAlert("Violation Action submitted successfully!");

            // Reset form
            violationAction.value = {
                violation_id: '',
                truck_id: '',
                trailer_id: '',
                loading_order_item_id: '',
                description: '',
                time: '',
                location: '',
                employee_id: '',
                parameters: {}
            };

            // Reset search fields
            employeeSearch.value = '';
            truckSearch.value = '';
            trailerSearch.value = '';
            loadingOrderSearch.value = '';
        } else {
            showErrorAlert(response.data.message || "Error submitting report");
        }
    } catch (error) {
        console.error("Submission error:", error);

        let errorMessage = "Failed to submit violation action";
        if (error.response) {
            if (error.response.data?.errors) {
                errorMessage = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                    .join('\n');
            } else if (error.response.data?.message) {
                errorMessage = error.response.data.message;
            }
        } else if (error.request) {
            errorMessage = "Network error - please check your connection";
        }

        showErrorAlert(errorMessage);
    } finally {
        loading.value = false;
    }
};

onMounted(async () => {
    isLoading.value = true;
    try {
        await Promise.all([
            loadActionsTaken(),
            fetchLoadingOrder()
        ]);

        // Load all required data in parallel
        const [violationsRes, actionsRes, paramsRes, driversRes, trucksRes, trailersRes] = await Promise.all([
            axios.get(`${BASE_URL}/violations`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }),
            axios.get(`${BASE_URL}/violation-actions`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }),
            axios.get(`${BASE_URL}/violation-parameters`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }),
            axios.get(`${BASE_URL_2}/employee/driver/show`),
            axios.get(`${BASE_URL_2}/vehicle/truck/show`),
            axios.get(`${BASE_URL_2}/vehicle/trailer/show`)
        ]);

        violations.value = violationsRes.data;
        violationParameters.value = paramsRes.data;

        // Handle different possible response structures
        drivers.value = driversRes.data.drivers || driversRes.data.data || driversRes.data || [];
        trucks.value = trucksRes.data.trucks || trucksRes.data.data || trucksRes.data || [];
        trailers.value = trailersRes.data.trailers || trailersRes.data.data || trailersRes.data || [];

        // Transform actions data with all relationships
        violationActions.value = actionsRes.data.data.map(action => ({
            ...action,
            violation: violations.value.find(v => v.id === action.violation_id) || null
        }));
        // console.log('Violation Actions responded:', violationActions.value);
        console.log('Violation Actions responded:', response.status);

    } catch (err) {
        console.error("Failed to load data:", err);
    } finally {
        isLoading.value = false;
    }
});

const clearFilters = () => {
    startDate.value = '';
    endDate.value = '';
    selectedViolationId.value = '';
    searchValue.value = '';
};
</script>

<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Reports</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="mb-0">Violation Action Reports. <strong>You have {{ violationActions.length }}
                                    Reports.</strong></h6>
                            <button class="btn btn-primary d-flex align-items-center" @click="openViolationModal">
                                Add Violation Event
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-plus ms-2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>

                            <!-- <router-link to="/violation/action/add"
                                class="btn btn-primary d-flex align-items-center">
                                Add Violation Event
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-plus ms-2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </router-link> -->
                        </div>

                        <!-- Filter controls -->
                        <div class="report-header mt-3">
                            <div class="filter-row row g-2 mb-2">
                                <div class="col-12 col-md-6 col-lg-4">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>From:</label>
                                        <input type="date" v-model="startDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-4">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>To:</label>
                                        <input type="date" v-model="endDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-4">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>Violation:</label>
                                        <select v-model="selectedViolationId" class="form-control">
                                            <option value="">All Violations</option>
                                            <option v-for="violation in violations" :key="violation.id"
                                                :value="violation.id">
                                                {{ violation.title }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="filter-row row g-2 mb-3">
                                <div class="col-12 col-md-6 col-lg-9">
                                    <input type="text" v-model="searchValue" placeholder="Search..."
                                        class="form-control">
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <button class="btn btn-danger w-100" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="d-flex align-items-center mb-3">
                            <span class="me-2">Violation Actions per page:</span>
                            <select v-model="itemsPerPage" class="form-select form-select-sm" style="width: 80px;">
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </select>
                        </div>

                        <!-- Loading state -->
                        <div v-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading reports...</p>
                        </div>

                        <!-- Data table -->
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>S/N</th>
                                        <th>Date</th>
                                        <th>Driver</th>
                                        <th>Truck</th>
                                        <th>Trailer</th>
                                        <th>Violation</th>
                                        <th>Action Taken</th>
                                        <th v-for="param in filteredParams" :key="param.id">
                                            {{ param.name }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(action, index) in paginatedActions" :key="action.id || index">
                                        <td>{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                                        <td>{{ formatDate(action.created_at) }}</td>
                                        <!-- <td>{{ action.employee }}</td> -->
                                        <td>{{ getDriverName(action.employee_id) }}</td>
                                        <!-- <td>{{ action.truck }}</td> -->
                                        <td>{{ getTruckNumber(action.truck_id) }}</td>
                                        <!-- <td>{{ action.trailer }}</td> -->
                                        <td>{{ getTrailerNumber(action.trailer_id) }}</td>
                                        <td>{{ action.violation?.title || 'N/A' }}</td>
                                        <td>{{ action.taken_action?.name || '—' }}</td>
                                        <td v-for="param in filteredParams" :key="param.id">
                                            {{ action.parameters?.[param.name] || '—' }}
                                        </td>
                                    </tr>
                                    <tr v-if="filteredActions.length === 0">
                                        <td :colspan="7 + filteredParams.length" class="text-center text-muted">
                                            No reports found matching your criteria
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Pagination -->
                            <div class="paginating-container pagination-solid flex-column align-items-center mt-4">
                                <ul role="menubar" aria-disabled="false" aria-label="Pagination"
                                    class="pagination mb-4 b-pagination">
                                    <li role="presentation" class="page-item first"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="firstPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to first page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item prev"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="prevPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to previous page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15 19l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>

                                    <!-- Dynamic page numbers -->
                                    <li role="presentation" class="page-item" v-if="showStartEllipsis">
                                        <span class="page-link">...</span>
                                    </li>
                                    <li role="presentation" class="page-item" v-if="visiblePages[0] > 1">
                                        <button role="menuitemradio" type="button" @click="goToPage(1)"
                                            class="page-link">
                                            1
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item" v-for="page in visiblePages" :key="page"
                                        :class="{ active: currentPage === page }">
                                        <button role="menuitemradio" type="button" @click="goToPage(page)"
                                            :aria-label="`Go to page ${page}`" :aria-checked="currentPage === page"
                                            :tabindex="currentPage === page ? 0 : -1" class="page-link">
                                            {{ page }}
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item"
                                        v-if="visiblePages[visiblePages.length - 1] < totalPages">
                                        <button role="menuitemradio" type="button" @click="goToPage(totalPages)"
                                            class="page-link">
                                            {{ totalPages }}
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item" v-if="showEndEllipsis">
                                        <span class="page-link">...</span>
                                    </li>

                                    <li role="presentation" class="page-item next"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="nextPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to next page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M9 5l7 7-7 7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item last"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="lastPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to last page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="modal animated fadeInRight" id="addViolationActionModal" tabindex="-1" role="dialog"
        aria-labelledby="faderightModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="faderightModalLabel">Add Violation Action</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body">
                    <form @submit.prevent="submitReport">
                        <!-- Violation Select -->
                        <div class="form-group">
                            <label for="violation">Select Violation</label>
                            <multiselect v-model="violationAction.violation_id" :options="filteredViolations"
                                :searchable="true" :close-on-select="true" :clear-on-select="false"
                                :preserve-search="true" placeholder="Select a Violation" label="title" track-by="id">
                            </multiselect>
                        </div>

                        <div class="row mt-3">
                            <!-- Left -->
                            <div class="col-md-6">
                                <div class="form-group mt-3">
                                    <label for="employee">Select Violator</label>
                                    <multiselect v-model="violationAction.employee_id" :options="filteredDrivers"
                                        :searchable="true" :close-on-select="true" :clear-on-select="false"
                                        :preserve-search="true" placeholder="Select a Violator" label="driver"
                                        track-by="employee_id">
                                    </multiselect>
                                </div>

                                <div class="form-group mt-3">
                                    <label for="truck">Truck</label>
                                    <multiselect v-model="violationAction.truck_id" :options="filteredTrucks"
                                        :searchable="true" :close-on-select="true" :clear-on-select="false"
                                        :preserve-search="true" placeholder="Select a Truck" label="registration_number"
                                        track-by="id" />
                                </div>

                                <div class="form-group mt-3">
                                    <label for="trailer">Trailer</label>
                                    <multiselect v-model="violationAction.trailer_id" :options="filteredTrailers"
                                        :searchable="true" :close-on-select="true" :clear-on-select="false"
                                        :preserve-search="true" placeholder="Select a Trailer"
                                        label="registration_number" track-by="id" />
                                </div>
                            </div>

                            <!-- Right -->
                            <div class="col-md-6">
                                <div class="form-group mt-3">
                                    <label for="loading_order_item">Loading Order</label>
                                    <multiselect v-model="violationAction.loading_order_item_id"
                                        :options="filteredLoadingOrders" :searchable="true" :close-on-select="true"
                                        :clear-on-select="false" :preserve-search="true"
                                        placeholder="Select a Loading Order" :custom-label="loadingOrderLabel"
                                        track-by="id" />
                                </div>

                                <div class="form-group mt-3">
                                    <label for="actionTaken">Action Taken</label>
                                    <multiselect v-model="violationAction.taken_action_id"
                                        :options="filteredActionsTaken" :searchable="true" :close-on-select="true"
                                        :clear-on-select="false" :preserve-search="true"
                                        placeholder="Select an Action Taken" label="name" track-by="id" />
                                </div>

                                <div v-if="parameters.length" class="row">
                                    <div v-for="param in parameters" :key="param.id" class="col-md-6 mt-3">
                                        <label :for="'param-' + param.id">{{ param.name }}</label>

                                        <input v-if="param.datatype === 'text'" type="text" class="form-control"
                                            v-model="violationAction.parameters[param.name]" :id="'param-' + param.id">

                                        <input v-else-if="param.datatype === 'number'" type="number"
                                            class="form-control" v-model.number="violationAction.parameters[param.name]"
                                            :id="'param-' + param.id">

                                        <input v-else-if="param.datatype === 'datetime-local'" type="datetime-local"
                                            class="form-control" v-model="violationAction.parameters[param.name]"
                                            :id="'param-' + param.id">

                                        <input v-else-if="param.datatype === 'time'" type="time" class="form-control"
                                            v-model="violationAction.parameters[param.name]" :id="'param-' + param.id">

                                        <input v-else type="text" class="form-control"
                                            v-model="violationAction.parameters[param.name]" :id="'param-' + param.id">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn" data-bs-dismiss="modal">
                        <i class="flaticon-cancel-12"></i> Discard
                    </button>
                    <button type="submit" class="btn btn-primary" @click="submitReport" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                        {{ loading ? 'Processing...' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.report-header {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
}

.filter-row {
    align-items: center;
}

.filter-row label {
    white-space: nowrap;
    margin-bottom: 0;
    font-weight: 500;
}

.text-success {
    color: #28a745;
}

.text-warning {
    color: #ffc107;
}

.text-danger {
    color: #dc3545;
}

.table-responsive {
    overflow-x: auto;
}

@media print {

    .report-header,
    .breadcrumb-one {
        display: none !important;
    }
}

@media (max-width: 767.98px) {
    .filter-row>div {
        margin-bottom: 0.5rem;
    }
}
</style>